import React, { useEffect, useMemo, useState } from 'react';
import { DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Card, Col, Form, Input, notification, Row, Space, Spin, Typography } from 'antd';
import { useIntl } from 'react-intl';
import { ObtPartnerLegal, ObtUserRestriction } from '../../../__generated__/types';
import {
    useUpdateCompanyLeitwegIdMutation,
    useRemoveCompanyLeitwegIdMutation,
} from '../obtMutations.generated';
import { useObt_UpdateLietwegsLegalChannelMutation } from '../obtQueries.generated';

// ─── Constants ─────────────────────────────────────────────────────────────────

const LEGAL_TAB_NAME = 'GERMANY';

// ─── Types ─────────────────────────────────────────────────────────────────────

type ObtGermanyLeitwegsFormProps = {
    partnerLegalData: ObtPartnerLegal;
    partnerno: string;
    companycode: string;
    idpartner: number;
    canUpdate: boolean;
    userRestrictions: ObtUserRestriction[];
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

function hasLegalRestrictionAccess(restrictions: ObtUserRestriction[], tabName: string): boolean {
    if (!restrictions || restrictions.length === 0) return true;
    return restrictions.some((r) => r.legalTabName === tabName);
}

function parseLeitwegsId(leitwegsId: string | null | undefined): { rough: string; fine: string; check: string } {
    if (!leitwegsId) return { rough: '', fine: '', check: '' };
    const parts = leitwegsId.split('-');
    if (parts.length === 3) return { rough: parts[0], fine: parts[1], check: parts[2] };
    if (parts.length === 2) return { rough: parts[0], fine: '', check: parts[1] };
    return { rough: leitwegsId, fine: '', check: '' };
}

function buildLeitwegsId(rough: string, fine: string, check: string): string {
    let id = rough;
    if (fine) id = `${id}-${fine}`;
    if (check) id = `${id}-${check}`;
    return id;
}

// ─── Validators ────────────────────────────────────────────────────────────────

function validateRoughAddress(_rule: unknown, value: string): Promise<void> {
    if (!value) return Promise.resolve();
    if (Number.isNaN(Number(value))) return Promise.reject('Must be numeric');
    if (value.length < 2 || value.length > 12) return Promise.reject('Must be 2–12 digits');
    return Promise.resolve();
}

function validateFineAddress(_rule: unknown, value: string): Promise<void> {
    if (!value) return Promise.resolve();
    if (value.length > 30) return Promise.reject('Max 30 characters');
    return Promise.resolve();
}

function validateCheckDigit(_rule: unknown, value: string): Promise<void> {
    if (!value) return Promise.resolve();
    if (Number.isNaN(Number(value))) return Promise.reject('Must be numeric');
    if (value.length !== 2) return Promise.reject('Must be exactly 2 digits');
    return Promise.resolve();
}

function validateEmail(_rule: unknown, value: string): Promise<void> {
    if (!value) return Promise.resolve();
    if (!value.match(/.+@.+/)) return Promise.reject('Invalid email');
    return Promise.resolve();
}

// ─── Component ─────────────────────────────────────────────────────────────────

const ObtGermanyLeitwegsForm: React.FC<ObtGermanyLeitwegsFormProps> = ({
    partnerLegalData,
    partnerno,
    companycode,
    idpartner,
    canUpdate,
    userRestrictions,
}) => {
    const intl = useIntl();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const [updateLeitweg] = useUpdateCompanyLeitwegIdMutation();
    const [deleteLeitweg] = useRemoveCompanyLeitwegIdMutation();
    const [updateLeitwegsChannel] = useObt_UpdateLietwegsLegalChannelMutation();

    const parsed = useMemo(() => parseLeitwegsId(partnerLegalData?.leitwegsId), [partnerLegalData?.leitwegsId]);

    useEffect(() => {
        form.setFieldsValue({
            roughaddress: parsed.rough,
            fineaddress: parsed.fine,
            checkdigit: parsed.check,
            leitwegsEmail: partnerLegalData?.leitwegsEmail ?? '',
        });
    }, [partnerLegalData, parsed, form]);

    // Live preview of the composed Leitwegs-ID
    const roughValue = Form.useWatch('roughaddress', form) ?? '';
    const fineValue = Form.useWatch('fineaddress', form) ?? '';
    const checkValue = Form.useWatch('checkdigit', form) ?? '';
    const composedLeitwegsId = buildLeitwegsId(roughValue, fineValue, checkValue);

    const canAccess = hasLegalRestrictionAccess(userRestrictions, LEGAL_TAB_NAME);
    const isDisabled = !canUpdate || !canAccess;

    const handleSave = async () => {
        if (!canAccess) {
            notification.error({
                message: intl.formatMessage({ id: 'obt.legal.restriction.error', defaultMessage: 'You do not have permission for this legal tab.' }),
            });
            return;
        }

        try {
            await form.validateFields();
        } catch {
            return;
        }

        const values = form.getFieldsValue();
        const leitwegsId = buildLeitwegsId(values.roughaddress || '', values.fineaddress || '', values.checkdigit || '');

        setLoading(true);
        try {
            await updateLeitweg({
                variables: {
                    leitwegid: leitwegsId,
                    leitwegsemail: values.leitwegsEmail || '',
                    idpartner,
                    partnerno,
                    companycode,
                },
            });

            // Auto-assign default channels on Leitwegs-ID save
            await updateLeitwegsChannel({
                variables: {
                    idPartner: idpartner,
                    partnerNo: partnerno,
                    companyCode: companycode,
                },
            });

            notification.success({
                message: intl.formatMessage({ id: 'obt.legal.germany.save.success', defaultMessage: 'Leitwegs-ID saved successfully.' }),
            });
        } catch (err) {
            console.error('Failed to save Leitwegs-ID:', err);
            notification.error({
                message: intl.formatMessage({ id: 'obt.legal.germany.save.error', defaultMessage: 'Failed to save Leitwegs-ID.' }),
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        setLoading(true);
        try {
            await deleteLeitweg({
                variables: { idpartner, partnerno, companycode },
            });
            form.resetFields();
            notification.success({
                message: intl.formatMessage({ id: 'obt.legal.germany.delete.success', defaultMessage: 'Leitwegs-ID deleted successfully.' }),
            });
        } catch (err) {
            console.error('Failed to delete Leitwegs-ID:', err);
            notification.error({
                message: intl.formatMessage({ id: 'obt.legal.germany.delete.error', defaultMessage: 'Failed to delete Leitwegs-ID.' }),
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Spin spinning={loading} size="large">
            <Form layout="vertical" onFinish={handleSave} form={form}>
                <Card
                    title={intl.formatMessage({ id: 'obt.legal.germany.title', defaultMessage: 'Leitwegs-ID' })}
                    size="small"
                    
                >
                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="roughaddress"
                                label={intl.formatMessage({ id: 'obt.legal.germany.roughaddress', defaultMessage: 'Rough Addressing' })}
                                rules={[
                                    { required: true, message: intl.formatMessage({ id: 'obt.legal.germany.roughaddress.required', defaultMessage: 'Required' }) },
                                    { validator: validateRoughAddress },
                                ]}
                            >
                                <Input
                                    placeholder={intl.formatMessage({ id: 'obt.legal.germany.roughaddress', defaultMessage: 'Rough Addressing' })}
                                    disabled={isDisabled}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="fineaddress"
                                label={intl.formatMessage({ id: 'obt.legal.germany.fineaddress', defaultMessage: 'Fine Addressing' })}
                                rules={[{ validator: validateFineAddress }]}
                            >
                                <Input
                                    placeholder={intl.formatMessage({ id: 'obt.legal.germany.fineaddress', defaultMessage: 'Fine Addressing' })}
                                    disabled={isDisabled}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="checkdigit"
                                label={intl.formatMessage({ id: 'obt.legal.germany.checkdigit', defaultMessage: 'Check Digit' })}
                                rules={[
                                    { required: true, message: intl.formatMessage({ id: 'obt.legal.germany.checkdigit.required', defaultMessage: 'Required' }) },
                                    { validator: validateCheckDigit },
                                ]}
                            >
                                <Input
                                    placeholder={intl.formatMessage({ id: 'obt.legal.germany.checkdigit', defaultMessage: 'Check Digit' })}
                                    disabled={isDisabled}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16} align="bottom">
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="leitwegsEmail"
                                label={intl.formatMessage({ id: 'obt.legal.germany.email', defaultMessage: 'Leitwegs Email' })}
                                rules={[
                                    { required: true, message: intl.formatMessage({ id: 'obt.legal.germany.email.required', defaultMessage: 'Required' }) },
                                    { validator: validateEmail },
                                ]}
                            >
                                <Input
                                    placeholder={intl.formatMessage({ id: 'obt.legal.germany.email', defaultMessage: 'Leitwegs Email' })}
                                    disabled={isDisabled}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label={intl.formatMessage({ id: 'obt.legal.germany.preview', defaultMessage: 'Leitwegs-ID Preview' })}>
                                <Typography.Text strong={true}>{composedLeitwegsId || '—'}</Typography.Text>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col>
                            <Space>
                                <Button
                                    icon={<SaveOutlined />}
                                    type="primary"
                                    htmlType="submit"
                                    disabled={isDisabled}
                                >
                                    {intl.formatMessage({ id: 'obt.legal.btn.save', defaultMessage: 'Save' })}
                                </Button>
                                <Button
                                    icon={<DeleteOutlined />}
                                    onClick={handleDelete}
                                    disabled={isDisabled || !partnerLegalData?.leitwegsId}
                                >
                                    {intl.formatMessage({ id: 'obt.legal.btn.delete', defaultMessage: 'Delete' })}
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>
            </Form>
        </Spin>
    );
};

export default ObtGermanyLeitwegsForm;

